// src/App.tsx
// -----------------------------------------------------------------------------
// Router for the Red Robin storefront. All pages render inside the config-driven
// Layout (header/nav/collections strip/footer). Routes:
//   /                         -> shop (grid, all products) - homepage IS the shop
//   /shop                     -> all products
//   /shop/:category           -> category-filtered grid
//   /collections/:collection  -> collection-filtered grid (Kaffe, Rant & Rave)
//   /product/:slug            -> product detail
//   /cart                     -> cart page (wired later; placeholder for the visit)
//   /:static                  -> simple static pages (privacy/cookies/terms/etc.)
//
// The homepage is the shop grid for now (a boutique's front door is its products).
// A richer landing/hero can be added later without touching these routes.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import React from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ShopPage } from './pages/ShopPage';
import { ProductPage } from './pages/ProductPage';
import { AccountPage } from './pages/AccountPage';
import { CartPage } from './pages/CartPage';
import { shopConfig as cfg } from './shopConfig';
import { PrivacyPage, CookiesPage, TermsPage, ShippingPage } from './pages/LegalPages';
import { OurStoryPage } from './pages/OurStoryPage';
import { ContactPage } from './pages/ContactPage';
import { SuccessPage } from './pages/SuccessPage';

// simple static page (legal/info) - content stubbed, styled to match
const StaticPage: React.FC = () => {
  const { page } = useParams();
  const navigate = useNavigate();
  const title = (page || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 64px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary }}>{title}</h1>
      <p style={{ color: 'rgba(0,0,0,.6)', fontSize: 15, lineHeight: 1.7, marginTop: 12 }}>
        This page is coming soon. For any question in the meantime, please visit us at{' '}
        {cfg.contact.address_lines.join(', ')}.
      </p>
      <button onClick={() => navigate('/shop')} style={{ marginTop: 20, background: cfg.brand.accent, color: cfg.brand.secondary, border: 'none', padding: '10px 20px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Back to Shop</button>
    </div>
  );
};

// cart placeholder - real CartPage wired in the shoppable phase
const CartPlaceholder: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: 24, fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary }}>Your Cart</h1>
      <p style={{ color: 'rgba(0,0,0,.55)', fontSize: 15, marginTop: 12 }}>Checkout is being set up. Browse the collection in the meantime.</p>
      <button onClick={() => navigate('/shop')} style={{ marginTop: 20, background: cfg.brand.accent, color: cfg.brand.secondary, border: 'none', padding: '12px 24px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Continue Shopping</button>
    </div>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <Layout>
      <Routes>
        <Route path="/" element={<ShopPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/:category" element={<ShopPage mode="category" />} />
        <Route path="/group/:group" element={<ShopPage mode="group" />} />
        <Route path="/collections/:collection" element={<ShopPage mode="collection" />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/shipping" element={<ShippingPage />} />
        <Route path="/our-story" element={<OurStoryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/:page" element={<StaticPage />} />
      </Routes>
    </Layout>
  </BrowserRouter>
);

export default App;
